import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import {CustomSearch, PublicCustomSearch, SERVICE_ADVANCED_SEARCH} from '../reduxToolkit/Commons/CommonsApi';
import { CustomAddChip } from './CustomAddChip';
import {TABLES} from '../Apis/Tables';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle: Full Stack Developer - IBL
 * @Email: clopez@ibl.mx
 *
 * @param {string} autocompleteId - Unique ID for the autocomplete input.
 * @param {string} buttonId - Unique ID for the add button.
 * @param {string} label - Translation key used as the input label.
 * @param {Array<{ id: string, label: string }>} selectedList - List of selected items rendered as chips.
 * @param {function} setSelectedList - Callback to update selected items.
 * @param {Array} userTypeIdsSearch - User type IDs used to filter search results.
 * @param {boolean} [required=false] - Marks the field as required.
 * @param {boolean} [showRenderOption=true] - Enables custom rendering of autocomplete options.
 * @param {boolean} [hideButton=true] - If true, hides add button and auto-adds on selection.
 * @param {boolean} [isFetchOnSearch=true] - Enables API search while typing or triggering search.
 * @param {boolean} [disabled=false] - Disables input, button and chip interactions.
 * @param {string} api - API endpoint used for search.
 * @param {object|null} bodyApi - Custom request body for API search.
 * @param {number} [rowsPerPage=20] - Pagination size for search results.
 * @param {string} model - Model identifier used in API request.
 * @param {boolean} [emptyFields] - Indicates validation error state.
 * @param {ReactNode} [noOptionsCard] - Custom UI shown when no results exist.
 * @param {number|string} [maxLength] - Maximum number of selectable items.
 * @param {function} [reset] - Resets input and selected state.
 * @param {string|number} keyId - Field used as unique identifier for API results.
 * @param {boolean} [showSubSection] - Enables extra info rendering inside options.
 * @param {string} [helperText] - Helper text shown under input.
 * @param {boolean} [onlyOnKeyDown=false] - If true, search is triggered only on Enter key.
 * @param {boolean} [completeWidth=false] - Makes chips use full width.
 * @param {string} [classNameCustomCards] - Custom class for chip container.
 * @param {function} sanitizeDataList - Function that maps API response into autocomplete format.
 * @param {React.ElementType} [Icon] - Optional icon for options.
 * @param {React.ElementType} [chipIcon] - Custom icon for chips.
 * @param {string} [placeholder] - Input placeholder text.
 * @param {boolean} [cleanChipSearch] - Clears chip search input when true.
 * @param {function} [setCleanChipSearch] - Resets cleanChipSearch flag.
 * @param {object} [positionIconOptions] - Controls icon positioning inside options.
 * @param {boolean} [isValid=true] - Validation state of input.
 * @param {string} [isValidText] - Message key shown when validation fails.
 * @param {function} [isValidFunction] - Callback triggered on input change for validation.
 */
const CustomAddChipSearch = (
    {
        autocompleteId = 'slt_cacs_search',
        buttonId = 'btn_cacs_search',
        label = 'search',
        selectedList = [], // required
        setSelectedList, // required
        userTypeIdsSearch = [], // required
        required = false,
        showRenderOption = true,
        hideButton = true,
        isFetchOnSearch = true,
        disabled = false,
        api = SERVICE_ADVANCED_SEARCH, // required
        bodyApi = null,
        rowsPerPage = 20,
        model=TABLES.USER_BY_USER_TYPE,
        emptyFields,
        noOptionsCard = null,
        maxLength,
        reset,
        keyId,
        showSubSection,
        helperText='',
        onlyOnKeyDown=false,
        completeWidth=false,
        classNameCustomCards='',
        sanitizeDataList,
        Icon,
        chipIcon=null,
        placeholder,
        cleanChipSearch,
        setCleanChipSearch,
        positionIconOptions,
        isValid = true,
        isValidText,
        isValidFunction
    }
) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [options, setOptions] = useState([]);
    const [open, setOpen] = useState(false);

    const handleSearch = (search) => {
        if(!isValid) return;

        if (search.trim()) {
            handleApiSearch(search, userTypeIdsSearch);
        } else {
            setOptions([]);
            setOpen(false);
        }
    };

    const handleApiSearch = (search, userTypeIds) => {
        let body;
        if(bodyApi === null){
            body = {
                key: search,
                pagination: {
                    page: 0,
                    size: rowsPerPage,
                },
                userTypeIds: userTypeIds,
                model:model
            };
        }else{
            body= { ...bodyApi, key: search };
        }

        const searchAction = api.includes("public/")
            ? PublicCustomSearch
            : CustomSearch;

        dispatch(searchAction({ body, api }))
            .then(({ payload: { data: { data } } }) => {
                const content = data?.content ??
                                    (Array.isArray(data) && data.length > 0
                                        ? data
                                        : []);

                setOptions(sanitizeDataList(content, keyId, showSubSection, Icon));
                setOpen(true);
            });
    }

    return (
        <CustomAddChip
            autocompleteId={autocompleteId}
            buttonId={buttonId}
            label={t(label)}
            required={required}
            data={options}
            selectedList={selectedList}
            setSelectedList={setSelectedList}
            showRenderOption={showRenderOption}
            maxLength={maxLength}
            hideButton={hideButton}
            open={open}
            setOpen={setOpen}
            searchApi={(value) => {
                handleSearch(value);
            }}
            isFetchOnSearch={isFetchOnSearch}
            disabled={disabled}
            emptyFields={emptyFields}
            noOptionsCard={noOptionsCard}
            reset={reset}
            helperText={helperText}
            onlyOnKeyDown={onlyOnKeyDown}
            completeWidth={completeWidth}
            classNameCustomCards={classNameCustomCards}
            chipIcon={chipIcon}
            placeholder={placeholder}
            cleanChipSearch={cleanChipSearch}
            setCleanChipSearch={setCleanChipSearch}
            positionIconOptions={positionIconOptions}
            isValid={isValid}
            isValidText={isValidText}
            isValidFunction={isValidFunction}
        />
    );

};

export default CustomAddChipSearch
